/**
 * Invoice — PDF Document
 *
 * Keep in sync with apps/user-tenant/src/components/pdf/InvoicePdfDocument.tsx.
 * Both apps render the same invoice PDF against the shared `InvoicePdfData`
 * contract from @respectly/shared-types; this file is duplicated (not imported
 * cross-app) to avoid forcing @react-pdf/renderer into @respectly/ui.
 *
 * Frontend-rendered invoice using @react-pdf/renderer, consistent with
 * ConsentLogPdfDocument and AuditTrailPdfDocument (design tokens, band header,
 * fixed footer with page number).
 *
 * Sections:
 *   1. Header — company brand + invoice #
 *   2. Bill From / Bill To — static company info + tenant billing address
 *   3. Invoice meta — issued, period, due, status
 *   4. Line items table
 *   5. Totals — subtotal / tax / total / paid / balance
 *   6. Status stamp for paid invoices
 *   7. Footer — legal / page number
 */
import {
  Document,
  Page,
  Text,
  View,
  Font,
  StyleSheet,
} from '@react-pdf/renderer';
import { format, parseISO } from 'date-fns';
import type { InvoicePdfData, InvoicePdfLineItem } from '@respectly/shared-types';

Font.register({
  family: 'Helvetica',
  fonts: [
    { src: 'Helvetica' },
    { src: 'Helvetica-Bold', fontWeight: 'bold' },
    { src: 'Helvetica-Oblique', fontStyle: 'italic' },
  ],
});

const C = {
  primary: '#1d4ed8',
  primaryText: '#1e40af',
  green: '#166534',
  greenBg: '#dcfce7',
  red: '#991b1b',
  redBg: '#fee2e2',
  amber: '#92400e',
  amberBg: '#fef3c7',
  violet: '#5b21b6',
  violetBg: '#ede9fe',
  body: '#111827',
  muted: '#6b7280',
  border: '#e5e7eb',
  bg: '#f9fafb',
  white: '#ffffff',
} as const;

const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: C.body,
    backgroundColor: C.white,
    paddingBottom: 40,
  },
  headerBand: {
    backgroundColor: C.primary,
    paddingHorizontal: 36,
    paddingTop: 28,
    paddingBottom: 22,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  headerLeft: { flexDirection: 'column' },
  headerBrand: {
    fontSize: 11,
    color: '#bfdbfe',
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    marginBottom: 4,
    fontWeight: 'bold',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: C.white,
  },
  headerRight: { flexDirection: 'column', alignItems: 'flex-end' },
  headerInvoiceNumberLabel: {
    fontSize: 8.5,
    color: '#bfdbfe',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  headerInvoiceNumber: {
    fontSize: 14,
    color: C.white,
    fontWeight: 'bold',
  },
  body: {
    paddingHorizontal: 36,
    paddingTop: 20,
  },
  addressRow: {
    flexDirection: 'row',
    marginBottom: 18,
  },
  addressCol: {
    flex: 1,
    paddingRight: 16,
  },
  addressLabel: {
    fontSize: 8,
    color: C.muted,
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 6,
    fontWeight: 'bold',
  },
  addressName: {
    fontSize: 11,
    color: C.body,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  addressLine: {
    fontSize: 9.5,
    color: C.body,
    lineHeight: 1.4,
  },
  metaTable: {
    flexDirection: 'row',
    backgroundColor: C.bg,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 6,
    padding: 12,
    marginBottom: 18,
  },
  metaCell: { flex: 1, paddingRight: 8 },
  metaLabel: {
    fontSize: 8,
    color: C.muted,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  metaValue: {
    fontSize: 10,
    color: C.body,
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    color: C.muted,
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
    paddingBottom: 4,
  },
  table: {
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 4,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: C.bg,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  tableHeaderCell: {
    fontSize: 8,
    fontWeight: 'bold',
    color: C.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: C.border,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  tableRowLast: {
    flexDirection: 'row',
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  tableCell: { fontSize: 9.5, color: C.body },
  tableCellMuted: { fontSize: 8.5, color: C.muted },
  amountCell: { fontSize: 9.5, color: C.body, textAlign: 'right' },
  totals: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 14,
  },
  totalsBox: {
    width: 240,
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  totalsRowBorderTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderTopWidth: 1,
    borderTopColor: C.border,
    marginTop: 4,
  },
  totalsRowHighlight: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 10,
    backgroundColor: C.bg,
    borderRadius: 4,
    marginTop: 6,
  },
  totalsLabel: { fontSize: 9.5, color: C.muted },
  totalsValue: { fontSize: 9.5, color: C.body },
  totalsLabelBold: { fontSize: 10.5, color: C.body, fontWeight: 'bold' },
  totalsValueBold: { fontSize: 12, color: C.body, fontWeight: 'bold' },
  badge: {
    alignSelf: 'flex-start',
    borderRadius: 9999,
    paddingHorizontal: 10,
    paddingVertical: 3,
  },
  badgeText: { fontSize: 8.5, fontWeight: 'bold', letterSpacing: 0.3 },
  typeChip: {
    alignSelf: 'flex-start',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 1.5,
  },
  typeChipText: { fontSize: 7.5, fontWeight: 'bold', letterSpacing: 0.3 },
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#f8fafc',
    borderTopWidth: 1,
    borderTopColor: C.border,
    paddingHorizontal: 36,
    paddingVertical: 10,
  },
  footerText: {
    fontSize: 7.5,
    color: C.muted,
    textAlign: 'center',
    lineHeight: 1.5,
  },
  pageNumber: {
    fontSize: 8,
    color: C.muted,
    textAlign: 'center',
    marginTop: 4,
  },
});

function fmt(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy');
  } catch {
    return dateStr;
  }
}

function money(amount: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; bg: string; color: string }> = {
    paid: { label: 'Paid', bg: C.greenBg, color: C.green },
    pending: { label: 'Pending', bg: C.amberBg, color: C.amber },
    overdue: { label: 'Overdue', bg: C.redBg, color: C.red },
    cancelled: { label: 'Cancelled', bg: C.bg, color: C.muted },
    refunded: { label: 'Refunded', bg: C.violetBg, color: C.violet },
    draft: { label: 'Draft', bg: C.bg, color: C.muted },
  };
  const v = map[status] ?? { label: status.toUpperCase(), bg: C.bg, color: C.muted };
  return (
    <View style={[s.badge, { backgroundColor: v.bg }]}>
      <Text style={[s.badgeText, { color: v.color }]}>{v.label.toUpperCase()}</Text>
    </View>
  );
}

function TypeChip({ type }: { type: string }) {
  const map: Record<string, { label: string; bg: string; color: string }> = {
    subscription: { label: 'Subscription', bg: '#dbeafe', color: C.primaryText },
    addon: { label: 'Add-on', bg: C.violetBg, color: C.violet },
    overage: { label: 'Overage', bg: C.amberBg, color: C.amber },
    credit: { label: 'Credit', bg: C.greenBg, color: C.green },
  };
  const v = map[type] ?? { label: type, bg: C.bg, color: C.muted };
  return (
    <View style={[s.typeChip, { backgroundColor: v.bg }]}>
      <Text style={[s.typeChipText, { color: v.color }]}>{v.label.toUpperCase()}</Text>
    </View>
  );
}

function Header({ data }: { data: InvoicePdfData }) {
  return (
    <View style={s.headerBand}>
      <View style={s.headerLeft}>
        <Text style={s.headerBrand}>Respectly</Text>
        <Text style={s.headerTitle}>Invoice</Text>
      </View>
      <View style={s.headerRight}>
        <Text style={s.headerInvoiceNumberLabel}>Invoice No.</Text>
        <Text style={s.headerInvoiceNumber}>{data.invoice.invoiceNumber}</Text>
      </View>
    </View>
  );
}

function AddressBlocks({ data }: { data: InvoicePdfData }) {
  const t = data.tenant;
  const billedTo = t.billingName ?? t.name;
  const cityLine = [t.billingCity, t.billingProvince].filter(Boolean).join(', ');
  return (
    <View style={s.addressRow}>
      <View style={s.addressCol}>
        <Text style={s.addressLabel}>From</Text>
        <Text style={s.addressName}>Respectly</Text>
        <Text style={s.addressLine}>billing@respectly.ai</Text>
        <Text style={s.addressLine}>https://respectly.ai</Text>
      </View>
      <View style={s.addressCol}>
        <Text style={s.addressLabel}>Billed To</Text>
        <Text style={s.addressName}>{billedTo}</Text>
        {t.billingStreet1 ? <Text style={s.addressLine}>{t.billingStreet1}</Text> : null}
        {cityLine ? <Text style={s.addressLine}>{cityLine}</Text> : null}
        {t.billingPostalCode ? <Text style={s.addressLine}>{t.billingPostalCode}</Text> : null}
        {t.billingCountry ? <Text style={s.addressLine}>{t.billingCountry}</Text> : null}
      </View>
    </View>
  );
}

function Meta({ data }: { data: InvoicePdfData }) {
  const inv = data.invoice;
  const period =
    inv.periodStart && inv.periodEnd
      ? `${fmt(inv.periodStart)} — ${fmt(inv.periodEnd)}`
      : '—';
  return (
    <View style={s.metaTable}>
      <View style={s.metaCell}>
        <Text style={s.metaLabel}>Issued</Text>
        <Text style={s.metaValue}>{fmt(inv.createdAt)}</Text>
      </View>
      <View style={s.metaCell}>
        <Text style={s.metaLabel}>Billing Period</Text>
        <Text style={s.metaValue}>{period}</Text>
      </View>
      <View style={s.metaCell}>
        <Text style={s.metaLabel}>Due Date</Text>
        <Text style={s.metaValue}>{fmt(inv.dueDate)}</Text>
      </View>
      <View style={s.metaCell}>
        <Text style={s.metaLabel}>Status</Text>
        <View style={{ marginTop: 2 }}>
          <StatusBadge status={inv.status} />
        </View>
      </View>
    </View>
  );
}

function LineItems({ items, currency }: { items: InvoicePdfLineItem[]; currency: string }) {
  return (
    <View>
      <Text style={s.sectionTitle}>Line Items</Text>
      <View style={s.table}>
        <View style={s.tableHeader}>
          <Text style={[s.tableHeaderCell, { flex: 3 }]}>Description</Text>
          <Text style={[s.tableHeaderCell, { flex: 1.2 }]}>Type</Text>
          <Text style={[s.tableHeaderCell, { flex: 0.6, textAlign: 'right' }]}>Qty</Text>
          <Text style={[s.tableHeaderCell, { flex: 1.2, textAlign: 'right' }]}>Unit</Text>
          <Text style={[s.tableHeaderCell, { flex: 1.2, textAlign: 'right' }]}>Amount</Text>
        </View>
        {items.length === 0 ? (
          <View style={s.tableRowLast}>
            <Text style={[s.tableCellMuted, { flex: 1, fontStyle: 'italic', textAlign: 'center' }]}>
              No line items
            </Text>
          </View>
        ) : (
          items.map((item, i) => {
            const last = i === items.length - 1;
            return (
              <View key={i} style={last ? s.tableRowLast : s.tableRow}>
                <Text style={[s.tableCell, { flex: 3 }]}>{item.description}</Text>
                <View style={{ flex: 1.2 }}>
                  <TypeChip type={item.type} />
                </View>
                <Text style={[s.amountCell, { flex: 0.6 }]}>{item.quantity}</Text>
                <Text style={[s.amountCell, { flex: 1.2 }]}>{money(item.unitPrice, currency)}</Text>
                <Text style={[s.amountCell, { flex: 1.2, fontWeight: 'bold' }]}>
                  {money(item.total, currency)}
                </Text>
              </View>
            );
          })
        )}
      </View>
    </View>
  );
}

function Totals({ data }: { data: InvoicePdfData }) {
  const inv = data.invoice;
  const c = inv.currency || 'USD';
  return (
    <View style={s.totals}>
      <View style={s.totalsBox}>
        <View style={s.totalsRow}>
          <Text style={s.totalsLabel}>Subtotal</Text>
          <Text style={s.totalsValue}>{money(inv.subtotal, c)}</Text>
        </View>
        <View style={s.totalsRow}>
          <Text style={s.totalsLabel}>Tax</Text>
          <Text style={s.totalsValue}>{money(inv.tax, c)}</Text>
        </View>
        <View style={s.totalsRowBorderTop}>
          <Text style={s.totalsLabelBold}>Total</Text>
          <Text style={s.totalsValueBold}>{money(inv.total, c)}</Text>
        </View>
        <View style={s.totalsRow}>
          <Text style={s.totalsLabel}>Amount Paid</Text>
          <Text style={s.totalsValue}>{money(inv.amountPaid, c)}</Text>
        </View>
        <View style={s.totalsRowHighlight}>
          <Text style={s.totalsLabelBold}>
            {inv.status === 'paid' ? 'Balance' : 'Amount Due'}
          </Text>
          <Text style={[s.totalsValueBold, { color: inv.amountDue > 0 ? C.red : C.green }]}>
            {money(inv.amountDue, c)}
          </Text>
        </View>
      </View>
    </View>
  );
}

function PaidStamp({ paidAt }: { paidAt: string | null }) {
  if (!paidAt) return null;
  return (
    <View
      style={{
        marginTop: 18,
        padding: 14,
        backgroundColor: C.greenBg,
        borderWidth: 1,
        borderColor: '#86efac',
        borderRadius: 6,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Text style={{ fontSize: 14, fontWeight: 'bold', color: C.green, letterSpacing: 2 }}>
        PAID
      </Text>
      <Text style={{ fontSize: 9, color: C.green }}>on {fmt(paidAt)}</Text>
    </View>
  );
}

function Footer() {
  return (
    <View style={s.footer} fixed>
      <Text style={s.footerText}>
        Thank you for your business. Questions about this invoice? Reach us at billing@respectly.ai.
      </Text>
      <Text
        style={s.pageNumber}
        render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
        fixed
      />
    </View>
  );
}

export function InvoicePdfDocument({ data }: { data: InvoicePdfData }) {
  const currency = data.invoice.currency || 'USD';
  return (
    <Document
      title={`Invoice ${data.invoice.invoiceNumber}`}
      author="Respectly"
      subject={`Invoice ${data.invoice.invoiceNumber} — ${data.tenant.name}`}
      creator="Respectly"
    >
      <Page size="LETTER" style={s.page}>
        <Header data={data} />
        <View style={s.body}>
          <AddressBlocks data={data} />
          <Meta data={data} />
          <LineItems items={data.invoice.lineItems} currency={currency} />
          <Totals data={data} />
          {data.invoice.status === 'paid' ? <PaidStamp paidAt={data.invoice.paidAt} /> : null}
        </View>
        <Footer />
      </Page>
    </Document>
  );
}
